import React from 'react';
import { Target, Eye, Award, Zap } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const About = () => {
  const values = [
    {
      icon: Zap,
      title: "Réactivité",
      description: "Interventions rapides 24h/24 – 7j/7 pour répondre à vos urgences"
    },
    {
      icon: Award,
      title: "Qualité",
      description: "Matériel certifié et personnel hautement qualifié"
    },
    {
      icon: Target,
      title: "Sécurité",
      description: "Respect strict des normes industrielles et de sécurité"
    },
    {
      icon: Eye,
      title: "Disponibilité",
      description: "Présence constante pour urgences et maintenance préventive"
    }
  ];

  const objectives = [
    "Réduire les temps d'immobilisation des équipements clients",
    "Fournir des solutions techniques adaptées et fiables sur l'ensemble de la chaîne de valeur",
    "Accompagner les chantiers BTP et les installations industrielles les plus exigeantes",
    "Être un partenaire de référence en Afrique de l'Ouest pour le BTP, la mécanique, l'hydraulique et la location de matériel"
  ];

  const expertises = [
    "Travaux de génie civil",
    "Terrassement",
    "Construction et réhabilitation d'ouvrages",
    "Mécanique générale",
    "Tournage et usinage de pièces mécaniques",
    "Fourniture et réparation de vérins hydrauliques",
    "Fabrication et réparation de flexibles hydrauliques",
    "Maintenance d'équipements",
    "Location de matériel et d'engins",
  ];

  return (
    <section id="apropos" className="py-24 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Intro */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            À propos d'<span className="text-green-600">AGIPLUS CI</span>
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            AGIPLUS CI est une entreprise spécialisée dans les solutions techniques destinées aux secteurs du BTP, de l'industrie, de l'énergie, des mines et des infrastructures.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Initialement reconnue pour son expertise dans l'hydraulique, l'entreprise a progressivement développé une offre globale lui permettant d'accompagner ses clients sur l'ensemble de leurs besoins techniques.
          </p>
        </div>

        {/* Expertises */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Nos activités couvrent aujourd'hui</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {expertises.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="bg-green-100 text-green-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Award className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mt-6 text-center">
                Grâce à une équipe qualifiée et réactive, AGIPLUS CI apporte des solutions adaptées aux exigences des chantiers et des installations industrielles les plus exigeantes.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Vision */}
        <div className="mb-16">
          <Card className="bg-white border-green-200">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 p-4 rounded-lg">
                  <Eye className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Notre Ambition</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Être un partenaire de référence en Afrique de l'Ouest pour les besoins en BTP, mécanique, hydraulique et location de matériel, avec un engagement constant en matière de qualité, sécurité et satisfaction client.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-10">Nos Valeurs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-white hover:shadow-lg transition-shadow border-gray-200">
                <CardContent className="p-6">
                  <div className="bg-gradient-to-br from-green-50 to-orange-50 p-4 rounded-lg w-fit mb-4">
                    <value.icon className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Objectives */}
        <div>
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-10">Nos Objectifs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {objectives.map((objective, index) => (
              <div key={index} className="flex items-start gap-4 bg-white p-6 rounded-lg border border-gray-200">
                <div className="bg-orange-500 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-gray-700 leading-relaxed">{objective}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Info */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-green-50 to-orange-50 border-none">
            <CardContent className="p-6">
              <p className="text-gray-700">
                <span className="font-semibold">AGIPLUS CI</span> – SARLU au capital de 5 000 000 FCFA<br />
                RCCM : CI-ABJ-03-2025-B13-10106
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};